import { getConfig, setConfig } from '@qbe/qal';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const isPayMyBill = () => /\/pay-my-bill/.test(window.location.pathname);
/**
 * Update QAL configuration for Pay My Bill page if needed.
 *
 * @returns {boolean} True if the configuration is overridden, otherwise false.
 */
export const updatePayMyBillConfig = (): boolean => {
  // Check if the current page is related to paying a bill
  if (isPayMyBill()) {
    // Override the configuration using setConfig
    setConfig({
      basePagePath: '/au/payment/business/pay-my-bill',
      appName: 'PayMyBill-DigiSME',
      appProduct: 'PayMyBill-SME',
    });
    return true;
  }
  return false;
};

export const getApiStatus = (type: string) => type?.split('/').pop();

export const getApiName = (type: string) => {
  if (!type.includes('/')) {
    return type; // If no slash found, return the original string
  }

  const apiWithoutStatus = type.slice(0, type.lastIndexOf('/'));
  return apiWithoutStatus;
};

export const isFormStartStarted = (): boolean => {
  const { formEngagement } = getConfig();
  const { formEngaged } = formEngagement;

  return formEngaged;
};

export const getTrackPayload = (apiName: string, apiEndpoint: string, requestId: string) => ({
  data: {
    apiName,
    apiEndpoint,
    requestId,
  },
});

/** Reuse your existing payload type */
type TrackData = ReturnType<typeof getTrackPayload>['data'];

/** Generic guard: is object-like and indexable */
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** RTKQ: FetchBaseQueryError has a 'status' discriminant */
const isFetchBaseQueryError = (e: unknown): e is FetchBaseQueryError =>
  isRecord(e) && 'status' in e;

/** Redux Toolkit SerializedError */
const isSerializedError = (e: unknown): e is SerializedError =>
  isRecord(e) && ('message' in e || 'name' in e || 'stack' in e || 'code' in e);

/** Strip very simple HTML payloads into readable text */

export const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || '';
};

/** Safely JSON-stringify unknown values */
const safeStringify = (value: unknown): string => {
  try {
    if (value instanceof Error) {
      // include non-enumerable props
      return JSON.stringify(value, Object.getOwnPropertyNames(value));
    }
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/** Extract status as a string */
const getStatusString = (e: unknown): string => {
  if (isFetchBaseQueryError(e)) {
    return String(e.status);
  }
  return '(not set)';
};

/** Extract server-side message from FetchBaseQueryError.data */
// utils/extractServerMessage.ts

const handleStringData = (raw: string): string => {
  const trimmed = raw.trim();
  return trimmed.startsWith('<') ? stripHtml(trimmed) : trimmed;
};

const handleObjectData = (obj: Record<string, unknown>): string | undefined =>
  extractRootMessage(obj) ?? extractNestedErrorMessage(obj) ?? extractErrorsArrayMessages(obj);

const extractRootMessage = (obj: Record<string, unknown>): string | undefined =>
  typeof obj.message === 'string' ? obj.message : undefined;

const extractNestedErrorMessage = (obj: Record<string, unknown>): string | undefined => {
  const err = obj.error;
  return isRecord(err) && typeof err.message === 'string' ? err.message : undefined;
};

const extractErrorsArrayMessages = (obj: Record<string, unknown>): string | undefined => {
  const { errors } = obj;
  if (!Array.isArray(errors)) return undefined;

  const msgs = errors
    .map((item) => (isRecord(item) && typeof item.message === 'string' ? item.message : undefined))
    .filter((m): m is string => !!m && m.length > 0);

  return msgs.length ? msgs.join('; ') : undefined;
};

/**
 * Extract server-side message from FetchBaseQueryError.data
 */
export const extractServerMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string') return handleStringData(data);
  if (isRecord(data)) return handleObjectData(data);
  return undefined;
};

/** Extract message from SerializedError (Redux Toolkit) */
const extractSerializedMessage = (e: unknown): string | undefined => {
  if (isSerializedError(e)) {
    if (typeof e.message === 'string' && e.message) return e.message;
    if (typeof e.code === 'string' && e.code) return e.code;
  }
  return undefined;
};

/** Extract low-level network/parsing error for FetchBaseQueryError unions that carry 'error' */
const extractLowLevelError = (e: unknown): string | undefined => {
  if (!isFetchBaseQueryError(e)) return undefined;

  // Some discriminants ('PARSING_ERROR', 'CUSTOM_ERROR', 'FETCH_ERROR') include an 'error' string
  const rec = e as Record<string, unknown>;
  const val = rec.error;
  return typeof val === 'string' && val ? val : undefined;
};

/** Extract name/stack for analytics detail */
const extractName = (e: unknown): string | undefined => {
  if (e instanceof Error) return e.name;
  if (isSerializedError(e) && typeof e.name === 'string') return e.name;
  return undefined;
};

const extractStack = (e: unknown): string | undefined => {
  if (e instanceof Error) return e.stack;
  if (isSerializedError(e) && typeof e.stack === 'string') return e.stack;
  return undefined;
};

/**
 * Format error payload for analytics.trackApiError
 *  - Fully type-safe (no `any`)
 *  - Handles HTML/text/JSON error bodies
 *  - Normalises RTK Query error union
 */
export const formatTrackApiError = (error: unknown, baseData: TrackData) => {
  // Pull out specific bits from the RTKQ error union safely
  const status = getStatusString(error);
  const serverMsg = isFetchBaseQueryError(error) ? extractServerMessage(error.data) : undefined;
  const serializedMsg = extractSerializedMessage(error);
  const lowLevelErr = extractLowLevelError(error);

  // Decide final message with sensible precedence
  let message =
    serializedMsg ??
    serverMsg ??
    lowLevelErr ??
    // As a last resort, stringify the entire object to capture context
    safeStringify(error) ??
    '(not set)';

  // Enforce length limit
  if (message.length > 2000) {
    message = message.slice(0, 2000);
  }

  // Name/stack for errorResponse
  const name = extractName(error) ?? '(not set)';
  const stack = extractStack(error) ?? '(not set)';

  return {
    data: {
      ...baseData,
      errorCode: status,
      message,
      errorType: 'api',
      errorResponse: {
        errors: {
          name,
          message,
          stack,
        },
      },
    },
  };
};
